/**
 * Component: Widget Theme Application
 * SpecReferenceID: WIDGET-V1
 * Requirements: WIDGET-V1-R16
 *
 * Apply the admin-configured theme to the widget's root
 * element as CSS custom properties. Every theme key from
 * the ``WidgetConfig.theme`` dict becomes a single
 * ``--galen-<key>`` custom property on the root; the
 * widget's internal stylesheet references those custom
 * properties (set up in a later Batch C commit), so theme
 * overrides propagate to every rendered surface without
 * any host-side CSS customisation.
 *
 * R22 CSP note: the theme layer does NOT set inline style
 * attributes on every node. Each theme key becomes a
 * single entry on the widget root's ``style`` property;
 * host pages with ``style-src 'self'`` (plus
 * ``'unsafe-inline'`` for their own CSS-in-JS, which most
 * already have) can embed the widget without relaxing
 * their policy further.
 *
 * Design decisions:
 *
 *   - The custom-property namespace is ``--galen-<key>``
 *     so the widget's own styles cannot collide with host
 *     page CSS variables. Hosts overriding a specific
 *     token from outside the widget would set
 *     ``--galen-primary``, not ``--primary``.
 *   - Unknown / empty-string values are dropped rather
 *     than written as empty strings so a second
 *     ``applyTheme`` call with a shrunk theme dict removes
 *     stale variables from the previous apply. This keeps
 *     the future R08 re-init path (token refresh triggers
 *     a fresh config fetch) side-effect-free.
 *   - Theme keys are kebab-cased when written to the
 *     style; author-facing keys may be either kebab-case
 *     (``font-family``) or camelCase (``fontFamily``) for
 *     admin UI ergonomics.
 *   - AB#4408: brand defaults (``--galen-primary`` and its
 *     foreground / soft-tint companions) are seeded on the
 *     root before the admin theme is applied, so primary-
 *     coloured elements (Search button, chat send button,
 *     citation badges, …) render the Galen brand sky-blue
 *     out of the box without per-deployment theming. The
 *     values mirror the FRONTEND-WEB-V1 portal's ``--primary``
 *     (``hsl(199 89% 48%)``); an admin ``theme.primary``
 *     overrides the seed.
 */

/**
 * Galen brand defaults seeded onto the widget root by
 * ``applyTheme`` before the admin theme dict is applied.
 * Mirrors the portal's ``--primary`` (src/frontend/src/index.css).
 */
const BRAND_DEFAULTS: Record<string, string> = {
  '--galen-primary': 'hsl(199, 89%, 48%)',
  '--galen-primary-foreground': 'hsl(0, 0%, 100%)',
  '--galen-primary-soft': 'hsla(199, 89%, 48%, 0.12)',
};

/**
 * Apply the given theme dict to the widget root element.
 * Brand defaults are seeded first; the admin theme entries
 * then override any of them. Existing ``--galen-*``
 * properties not present in either the brand defaults or
 * the new dict are removed so shrinking the theme at admin
 * edit time actually unsets the variables.
 */
export function applyTheme(
  root: HTMLElement,
  theme: Record<string, string>,
): void {
  const existing = listGalenCustomProperties(root);
  const nextKeys = new Set<string>();

  // AB#4408: seed brand defaults. They go into nextKeys so the
  // cleanup pass below never removes them, and the admin-theme loop
  // below overwrites any of them the deployment has customised.
  for (const [name, value] of Object.entries(BRAND_DEFAULTS)) {
    nextKeys.add(name);
    root.style.setProperty(name, value);
  }

  for (const [rawKey, rawValue] of Object.entries(theme)) {
    const value = rawValue.trim();
    if (value.length === 0) continue;
    const key = toKebabCase(rawKey.trim());
    if (key.length === 0) continue;
    const fullName = `--galen-${key}`;
    nextKeys.add(fullName);
    root.style.setProperty(fullName, value);
  }

  for (const name of existing) {
    if (!nextKeys.has(name)) {
      root.style.removeProperty(name);
    }
  }
}

/**
 * Enumerate the ``--galen-*`` custom properties currently
 * set on an element's inline style. Used by ``applyTheme``
 * to clean up variables that the new theme no longer
 * declares.
 *
 * Exported for tests; callers should prefer ``applyTheme``
 * which does the diff-and-remove in one pass.
 */
export function listGalenCustomProperties(root: HTMLElement): string[] {
  const collected: string[] = [];
  for (let i = 0; i < root.style.length; i++) {
    const name = root.style.item(i);
    if (name.startsWith('--galen-')) {
      collected.push(name);
    }
  }
  return collected;
}

/**
 * Convert a camelCase key to kebab-case so
 * ``fontFamily`` and ``font-family`` both resolve to
 * ``--galen-font-family``. Lowercases the first letter of
 * every embedded uppercase run; already-kebab inputs are a
 * no-op.
 */
function toKebabCase(input: string): string {
  return input.replace(/[A-Z]+/g, (match, offset: number) => {
    const prefix = offset === 0 ? '' : '-';
    return prefix + match.toLowerCase();
  });
}
